import { Sequence } from '@repo/sdk/models/components';

import { ColumnOverlayProperties as OverlayProperties } from '@/components/sequencer-table/column-overlay';
import {
  findColumnWithValue,
  findDuplicateColumns,
  groupIdenticalColumns,
} from '@/components/sequencer-table/validations';
import { SequenceWithValues } from '@/features/equipment-browser/equipment-details/state/sequencer-editor.state';

export const trimBackground = `repeating-linear-gradient(135deg, #191919 0px, #191919 5px, #adff00 5px, #adff00 10px)`;
/**
 * Trim stripes for a user-sequence cell that Energy AI has taken over.
 *
 * neutral-925 is exactly the #191919 the active gradient above uses. The light
 * stripe is neutral-500 rather than neutral-600 - 600 is the literal nearest
 * token to the #52525b this replaced, but it is also the fill these cells sit
 * beside when Energy AI is active (see getCompressorSequencerCellStyles), so
 * matching it would leave the stripe pattern as the only thing telling a trim
 * cell apart from an on cell.
 */
export const inactiveTrimBackground = `repeating-linear-gradient(135deg, var(--color-ckds-neutral-925) 0px, var(--color-ckds-neutral-925) 5px, var(--color-ckds-neutral-500) 5px, var(--color-ckds-neutral-500) 10px)`;

/**
 * The stages of a compressor sequencer.
 */
export enum CompressorSequencerStage {
  UNAVAILABLE = 0,
  OFF = 1,
  ON = 2,
  TRIM = 3,
}

/**
 * Finds columns where too many compressors are on for the stage number.
 * A stage should have at most N compressors on, where N is the stage index.
 *
 * @param sequence - The sequence to validate
 * @returns Array of column indices with too many compressors
 */
export const findColumnsWithTooManyCompressors = (sequence: Sequence): number[] => {
  if (!sequence.table || sequence.table.length === 0) {
    return [];
  }

  const maxStages = Math.max(...sequence.table.map((item) => item.stageValues.length));
  const tooManyColumns: number[] = [];

  // Start at 1 to skip the 0th column
  for (let colIndex = 1; colIndex < maxStages; colIndex++) {
    const column = sequence.table.map((item) => item.stageValues[colIndex]);
    const compressorOnCount = column.filter((state) => {
      return state === CompressorSequencerStage.TRIM || state === CompressorSequencerStage.ON;
    }).length;

    // Stage index should have at most that many compressors on
    if (compressorOnCount > colIndex) {
      tooManyColumns.push(colIndex);
    }
  }

  return tooManyColumns;
};

/**
 * Finds columns that don't have any TRIM state.
 * Compressor sequencers should have at least one compressor in TRIM state per stage.
 *
 * @param sequence - The sequence to validate
 * @returns Array of column indices without trim
 */
export const findColumnsWithoutTrim = (sequence: Sequence): number[] => {
  if (!sequence.table || sequence.table.length === 0) {
    return [];
  }

  const maxStages = Math.max(...sequence.table.map((item) => item.stageValues.length));
  const noTrimColumns: number[] = [];

  // Start at 1 to skip the 0th column
  for (let colIndex = 1; colIndex < maxStages; colIndex++) {
    const column = sequence.table.map((item) => item.stageValues[colIndex]);
    const hasNoTrim = column.every((state) => state !== CompressorSequencerStage.TRIM);

    if (hasNoTrim) {
      noTrimColumns.push(colIndex);
    }
  }

  return noTrimColumns;
};

/**
 * Validates a compressor sequencer and returns overlay properties for each column.
 * This function checks for empty columns, duplicate columns, too many compressors, and missing trim.
 *
 * Priority order (higher priority overrides lower):
 * 1. Too Many Compressors
 * 2. No Trim
 * 3. Duplicates
 * 4. Empty (highest priority)
 *
 * @param sequence - The sequence to validate
 * @returns Array of overlay properties for each column
 */
export const getCompressorSequencerOverlayProperties = (
  sequence: Sequence | undefined
): (OverlayProperties | null)[] => {
  if (!sequence) {
    return [];
  }

  const maxStages = Math.max(...(sequence.table.map((item) => item.stageValues.length) || [0]));
  const overlays: (OverlayProperties | null)[] = Array.from({ length: maxStages }).map(() => null);

  // Find all validation issues
  const tooManyCompressors = findColumnsWithTooManyCompressors(sequence);
  const noTrimColumns = findColumnsWithoutTrim(sequence);
  const duplicateColumns = findDuplicateColumns(sequence);
  const duplicateGroups = groupIdenticalColumns(sequence);
  const emptyColumns = findColumnWithValue(sequence, [
    CompressorSequencerStage.OFF,
    CompressorSequencerStage.UNAVAILABLE,
  ]);

  // Apply overlays in priority order (lowest to highest priority)
  // 1. Too Many Compressors (lowest priority)
  tooManyCompressors.forEach((colIndex: number) => {
    overlays[colIndex] = {
      color: 'hsl(var(--ckds-warning))',
      message: 'Too Many',
    };
  });

  // 2. No Trim
  noTrimColumns.forEach((colIndex: number) => {
    overlays[colIndex] = {
      color: 'hsl(var(--ckds-warning))',
      message: 'No Trim',
    };
  });

  // 3. Duplicates
  duplicateColumns.forEach((colIndex: number) => {
    const group = duplicateGroups.find((g) => g.includes(colIndex));
    // Skip the first column in the group (it's the "original")
    if (group && group[0] === colIndex) {
      return;
    }
    overlays[colIndex] = {
      color: 'hsl(var(--ckds-warning))',
      message: `Same as Stage ${group?.[0]}`,
    };
  });

  // 4. Empty columns (highest priority - overrides all others)
  emptyColumns.forEach((colIndex: number) => {
    // Don't mark the 0th column as empty (it's always empty by design)
    if (colIndex === 0) {
      return;
    }
    overlays[colIndex] = {
      color: 'hsl(var(--ckds-warning))',
      message: 'Empty',
    };
  });

  return overlays;
};

/**
 * Derives the icon for a row in a compressor sequencer. We cannot do this in BFF,
 * because we need to know the actual value of the narrative var
 *
 * @param name - The name of the compressor sequencer.
 * @returns The icon for the compressor sequencer.
 */
export const deriveCompressorSequencerIcon = () => {
  return 'compressor' as const;
};

/**
 * The display string values of the stages of a compressor sequencer.
 */
const COMPRESSOR_SEQUENCER_STAGE_STRING_VALUES = ['Unavailable', 'Off', 'On', 'Trim'];

/**
 * Returns the string value of the compressor sequencer stage.
 *
 * @param val - The stage value to get the string value for.
 * @returns The string value of the compressor sequencer stage.
 */
export const getCompressorSequenceState = (stageValue: number): string => {
  if (stageValue === CompressorSequencerStage.OFF) {
    return COMPRESSOR_SEQUENCER_STAGE_STRING_VALUES[CompressorSequencerStage.OFF];
  }
  if (stageValue === CompressorSequencerStage.ON) {
    return COMPRESSOR_SEQUENCER_STAGE_STRING_VALUES[CompressorSequencerStage.ON];
  }
  if (stageValue === CompressorSequencerStage.TRIM) {
    return COMPRESSOR_SEQUENCER_STAGE_STRING_VALUES[CompressorSequencerStage.TRIM];
  }
  return COMPRESSOR_SEQUENCER_STAGE_STRING_VALUES[CompressorSequencerStage.UNAVAILABLE];
};

/**
 * Returns the cell styles for the compressor sequencer stage.
 * @param stageValue - The stage value to get the cell styles for.
 * @returns The cell styles for the compressor sequencer stage.
 */
export const getCompressorSequencerCellStyles = (
  stageValue: number,
  options?: { isEnergyAIActive?: boolean }
): { className: string; style: React.CSSProperties } => {
  const isEnergyAIActive = options?.isEnergyAIActive === true;
  const onClassName = isEnergyAIActive ? 'bg-ckds-neutral-600' : 'bg-ckds-brand-500';
  const trimStyle = {
    background: isEnergyAIActive ? inactiveTrimBackground : trimBackground,
  };

  return {
    className: ['bg-ckds-neutral-900', 'bg-ckds-neutral-900', onClassName][stageValue],
    style: [{}, {}, {}, trimStyle][stageValue],
  };
};

/**
 * Cycles the stage value of a compressor sequencer.
 *
 * @param currentStageValue - The current stage value to cycle.
 * @returns The new stage value.
 */
/**
 * Checks if a compressor (by name) is assigned in any sibling sequencer.
 * Returns the sibling's label if locked, or undefined if not.
 */
export const findLockingSibling = (
  compressorName: string,
  siblingSequences: SequenceWithValues[]
): string | undefined => {
  for (const sibling of siblingSequences) {
    const sequence = sibling.value as Sequence | undefined;
    if (!sequence?.table) continue;

    const isAssigned = sequence.table.some(
      (item) => item.name === compressorName && item.stageValues.some((v) => v !== 0)
    );

    if (isAssigned) {
      return sibling.label;
    }
  }
  return undefined;
};

export const cycleCompressorSequencerStageValue = (currentStageValue: number): number => {
  switch (currentStageValue) {
    case CompressorSequencerStage.OFF:
      return CompressorSequencerStage.ON;
    case CompressorSequencerStage.ON:
      return CompressorSequencerStage.TRIM;
    case CompressorSequencerStage.TRIM:
      return CompressorSequencerStage.OFF;
  }
  return currentStageValue;
};
